/**
 * Home hooks (F46) — one query for everything Home shows, its snooze, and the
 * CheckUp link a phone emails for a computer.
 *
 * `useHomeSummary` is the one read behind journey-web's Home and the phone's.
 * The caller passes `enabled`: both wait for the journey list, so they ask
 * once, for the journey on screen.
 */
import { useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import type { HomeCheckUpLinkResult, HomeSnoozeRequest, HomeSummary } from '@tonnie/types';

import { useApiClient } from '../providers/SdkProvider';
import { HomeService } from '../services/home.service';
import { homeKeys } from './home-keys';
import { talentInvitationKeys } from './useTalentInvitations';

export { homeKeys };

/**
 * How often Home asks again while the server says the score is being
 * calculated — the CheckUp job poll's own pace — so Home moves from
 * "calculating" to the journey dashboard by itself, with nothing to reload,
 * whether or not the CheckUp page is open to see the job finish (F26).
 */
export const HOME_CALCULATING_POLL_MS = 2000;

export function useHomeSummary(journeyId: string | null, options: { enabled: boolean }) {
  const client = useApiClient();
  const service = new HomeService(client);
  return useQuery<HomeSummary>({
    queryKey: homeKeys.summary(journeyId),
    queryFn: () => service.getSummary(journeyId ?? undefined),
    enabled: options.enabled,
    refetchInterval: (query) =>
      query.state.data?.checkUp.state === 'calculating' ? HOME_CALCULATING_POLL_MS : false,
  });
}

/**
 * "Later" on one Home item. The item leaves every cached Home at once — the
 * next ask moves up under the citizen's finger, not a round trip later — and
 * comes back if the request fails. Home is then refetched (the server decides
 * what is still shown), and an invitation's snooze also refreshes `mine`, the
 * list the invitation prompt and sheet read.
 */
export function useSnoozeHomeItem() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const service = new HomeService(client);
  return useMutation<void, Error, HomeSnoozeRequest, HomeSnapshot>({
    mutationFn: (request) => service.snooze(request),
    onMutate: async (request) => {
      await queryClient.cancelQueries({ queryKey: homeKeys.all });
      const snapshot = queryClient.getQueriesData<HomeSummary>({ queryKey: homeKeys.all });
      queryClient.setQueriesData<HomeSummary>({ queryKey: homeKeys.all }, (summary) =>
        summary?.needsYou
          ? {
              ...summary,
              needsYou: summary.needsYou.filter(
                (item) => !(item.kind === request.kind && item.subjectRef === request.subjectRef)
              ),
            }
          : summary
      );
      return snapshot;
    },
    onError: (_error, _request, snapshot) => {
      for (const [key, data] of snapshot ?? []) queryClient.setQueryData(key, data);
    },
    onSuccess: (_data, request) => {
      if (request.kind === 'invitation') {
        void queryClient.invalidateQueries({ queryKey: talentInvitationKeys.mine() });
      }
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey: homeKeys.all }),
  });
}

/** Every cached Home, as it was before an optimistic write. */
type HomeSnapshot = Array<[QueryKey, HomeSummary | undefined]>;

/**
 * Email the citizen the CheckUp link, for a computer (Citizen Mobile · D-M3).
 * Nothing on Home changes, so nothing is refetched; `emailed: false` tells the
 * caller the hub held it back and the share sheet is the way instead.
 */
export function useEmailCheckUpLink() {
  const client = useApiClient();
  const service = new HomeService(client);
  return useMutation<HomeCheckUpLinkResult, Error, void>({
    mutationFn: () => service.emailCheckUpLink(),
  });
}
